/* eslint-disable dot-notation */

import { AxiosResponse } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';

import { wizardModeOptions } from 'Models/Enums';
import { appConfigStore } from 'Models/AppConfigStore';
import { CapacityCategory } from 'Models/Shared/Capacity/CapacityCategory';
import { store } from 'Models/Store';
import {
	clearFerryBookingTransactionIdFromStorage,
	getFerryBookingTransactionIdFromStorage,
	setFerryBookingTransactionIdInStorage,
} from 'Services/Api/_HumanWritten/BookingService/BookingService';
import {
	alterBooking,
	BookingCreationIds,
	createBulkBooking,
	createFerryBooking,
	dataToBulkFerryTripBookingDto,
	dataToFerryTripBookingDto,
	discardAlterations,
	editCartBulkBooking,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { AlterationExpirationDto } from 'Models/Dtos/BookingDtos';
import { devLog } from 'Util/_HumanWritten/ConsoleLogUtils';
import alertToast from 'Util/ToastifyUtils';
import InvalidBulkTripsList from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Reservation/InvalidBulkTripsList';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { showRestartWizardModal } from 'Views/Components/_HumanWritten/Modal/RestartWizardModal/RestartWizardModal';
import { Colors } from 'Views/Components/Button/Button';
import { newAlertModal } from 'Views/Components/Modal/NewAlertModal';
import { BookingWizardData, ferryBookingWizardDataHasChanged } from '../../BookingWizardData/BaseBookingWizardData';
import { BulkBookingFilters } from 'Modules/BulkBooking/Types';
import { clearBookingWizardData, useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useParkingWizardStore } from 'Stores/ParkingWizardStore';
import {
	createParkingReservation,
	deleteParkingReservationsByTransactionId,
} from 'Services/Api/_HumanWritten/ParkingService';
import { InvalidBulkBookingErrorDto, NoAvailableSpaceExceptionDto } from './NoAvailableDto';
import { OverbookingModalManager } from './OverbookingModalManager';
import { OverbookingWarningDto } from './OverbookingWarningDto';
import { WaitingReservationLoader } from './WaitingReservationLoader';

export enum ReservationErrorType {
	Overbooked = 'Overbooked',
	OVERBOOKINGALLOWANCE = 'OverbookingAllowance',
	CARGO = 'Cargo',
	TOW_ON = 'TowOn',
	PASSENGER = 'Passenger',
	WEIGHT = 'Weight',
	OTHER = 'Other',
	NONE = 'None',
	LOADING = 'Loading',
	DISABLED_TRIP = 'DisabledFerryTrip',
	CLOSED = 'ClosedFerryTrip',
	UNAVAILABLE = 'Unavailable',
	PAYMENT_ALREADY_TAKEN = 'PaymentAlreadyTaken',
	/**
	 * The booking being edited is cancelled, departed or checked in, so it may not be changed at
	 * all. Distinct from CLOSED / UNAVAILABLE, which are about the trip being moved onto and can
	 * be resolved by picking another one — there is nothing to pick here.
	 */
	BOOKING_NOT_EDITABLE = 'BookingNotEditable',
	/**
	 * A seat the booker chose was taken before they finished. Hard, unlike a full class: two people
	 * cannot sit in one seat, so there is nothing for staff to override — the booker goes back to the
	 * seating step with the lost seats named.
	 */
	SEAT_UNAVAILABLE = 'SeatUnavailable',
	ADD_ON = 'AddOn',
}

export interface ReservationErrorInformation {
	reservationError: ReservationErrorType;
	invalidTrips: string[];
	message?: string;

	departingOverbookingWarnings?: OverbookingWarningDto[];
	returningOverbookingWarnings?: OverbookingWarningDto[];
	bulkBookingErrors?: InvalidBulkBookingErrorDto[];
	/** With SEAT_UNAVAILABLE: the seats that were lost, named as the booker chose them. */
	seatLabels?: string[];
}

export interface ReservationTabProps {
	wizardData: BookingWizardData;
	onUpdateWizardData: (newData: BookingWizardData) => void;
	navigateToNextStep: () => void;
	navigateToPreviousStep: () => void;
	navigateToTickets: () => void;
	onCompleteBookingAttempt: (attemptFailed: boolean) => void;
}

export function ReservationTab({
	wizardData,
	onUpdateWizardData,
	navigateToNextStep,
	navigateToPreviousStep,
	navigateToTickets,
	onCompleteBookingAttempt,
}: ReservationTabProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const baseUrl = '/booking-wizard';
	const [shownToast, setShownToast] = useState(false);
	const { oldWizardData, setOldWizardData, clearOldWizardData } = useBookingWizardDataStore();
	const parkingStore = useParkingWizardStore();
	const bookingCreationAttempted = useRef(false);

	const [errors, setErrors] = useState<ReservationErrorInformation>({
		reservationError: ReservationErrorType.LOADING,
		invalidTrips: [],
		message: '',
	});

	/**
	 * Leaves the wizard for the booking that was being edited, after a failure the customer cannot do
	 * anything about from inside the wizard.
	 *
	 * `clearBookingWizardData` first is load-bearing, not tidiness. useBookingWizardNavigationGuard
	 * blocks every route out of an in-progress ALTERATION to ask "this will discard any changes", and
	 * the way useNavigationBlocker blocks is to let the navigation happen and then navigate straight
	 * back. That remounts this step, which re-runs the reservation, which fails the same way, which
	 * reopens the modal on top of the discard prompt — and neither can then be dismissed. Clearing
	 * drops wizardMode back to CREATE, so the guard lets this one navigation through.
	 *
	 * The prompt has nothing to offer here anyway: the attempt has already failed, and the caller
	 * releases the alteration itself where there is one to release.
	 */
	const leaveWizardForBooking = (bookingId: string) => {
		clearBookingWizardData();

		// bookingToEdit is always set on an alteration, which is the only way to reach any of these
		// failures — the fallback is for the impossible case, not an expected one.
		if (stringNotEmpty(bookingId)) {
			navigate(`/bookings/${bookingId}`);
		} else {
			navigateToPreviousStep();
		}
	};

	/**
	 * The sentence shown for a trip-level refusal, and whether the refused trip is the one the booking
	 * is already on. When it is, there is no other trip to pick and the edit is over; otherwise the
	 * customer is sent back to the tickets tab to choose again.
	 *
	 * `reason` completes "...associated to the departing/returning booking ".
	 */
	const buildTripRefusal = (reason: string) => {
		let message =
			'A ferry trip you have selected is no longer available for ' +
			'online bookings. Please choose another trip time or contact staff.';
		let originalBookingTicketIsClosed = false;

		if (isNotNullOrUndefined(oldWizardData)) {
			const departureAffected = errors.invalidTrips.includes(oldWizardData.departureTicketId);
			const returnAffected = errors.invalidTrips.includes(oldWizardData.returningTicketId ?? '');

			if (departureAffected || returnAffected) {
				originalBookingTicketIsClosed = true;
				message = `Your booking can not be edited because the ferry trip associated to the ${
					departureAffected ? 'departing' : 'returning'
				} booking ${reason}`;
			}
		}

		return { message, originalBookingTicketIsClosed };
	};

	const clickOk = async (originalBookingTicketIsClosed: boolean) => {
		const newData = new BookingWizardData({ ...wizardData });

		// If the invalid tripId is the original booking tripId (that means they are
		// likely editing a booking that is on a trip that has already closed for modifications).
		if (!originalBookingTicketIsClosed) {
			errors?.invalidTrips.forEach(tripId => {
				if (newData.departureTicketId === tripId) {
					newData.departureTicketId = '';
				}
				if (newData.returningTicketId === tripId) {
					newData.returningTicketId = '';
				}
			});
			onUpdateWizardData(newData);
			navigateToTickets();
			return;
		}

		// Release the alteration this attempt reserved before leaving. Unlike the booking-not-editable
		// refusal, the closed/unavailable guard runs AFTER the transaction step, so there can be a
		// RESERVED alteration holding space on the trip. Skipping the wizard's own discard prompt (see
		// leaveWizardForBooking) means doing this here instead of leaving it to the prompt.
		await discardAlterations(navigate, {
			bookingIds: [newData.bookingToEdit ?? ''],
			systemExpired: false,
		} as AlterationExpirationDto);

		leaveWizardForBooking(newData.bookingToEdit ?? '');
	};

	// No discard: the server completed this transaction on its way to refusing (CheckPaymentNotTakenStep),
	// so cancel-alteration would answer "Payment already taken" and open a second copy of this modal.
	const navigateToBooking = async () => leaveWizardForBooking(wizardData.bookingToEdit ?? '');

	// Handle error toasts in useEffect to avoid setState during render
	useEffect(() => {
		if (shownToast) {
			return;
		}

		if (errors?.reservationError === ReservationErrorType.DISABLED_TRIP) {
			const handleConfirm = async () => {
				clearBookingWizardData();
				clearFerryBookingTransactionIdFromStorage();
				navigate('/booking-wizard/search');
			};
			showRestartWizardModal(handleConfirm);
			setShownToast(true);
		}

		// The booking itself can no longer be changed — cancelled, departed, or checked in. Nothing on
		// the previous step would fix that, so say which it is and put the customer back on the booking
		// they were editing, rather than dropping them into the wizard behind the generic "contact
		// staff" toast. Mirrors how PAYMENT_ALREADY_TAKEN already ends the flow.
		if (errors?.reservationError === ReservationErrorType.BOOKING_NOT_EDITABLE) {
			setShownToast(true);

			// Read before the wizard data is cleared by leaveWizardForBooking.
			const bookingId = wizardData.bookingToEdit ?? '';

			// No discard: CheckBookingAlterableStep refuses before the alteration transaction is
			// created, and entering the edit flow already discarded any earlier one.
			const returnToBooking = () => leaveWizardForBooking(bookingId);

			newAlertModal(
				'Booking can no longer be changed',
				errors.message ?? 'This booking can no longer be changed. Please contact staff.',
				{
					cancelText: 'Ok',
					buttonColour: Colors.Black,
				},
			).then(returnToBooking); // newAlertModal only ever resolves — it resolves on close too.
		}

		if (errors?.reservationError === ReservationErrorType.CLOSED) {
			setShownToast(true);

			const { message, originalBookingTicketIsClosed } = buildTripRefusal('is closed for modifications.');

			newAlertModal('Ferry trip closed', message, {
				cancelText: 'Ok',
				buttonColour: Colors.Black,
			}).then(() => clickOk(originalBookingTicketIsClosed));
		}

		if (errors?.reservationError === ReservationErrorType.UNAVAILABLE) {
			setShownToast(true);

			const { message, originalBookingTicketIsClosed } = buildTripRefusal(
				'is no longer available for online bookings.',
			);

			newAlertModal('Ferry trip unavailable', message, {
				cancelText: 'Ok',
				buttonColour: Colors.Black,
			}).then(() => clickOk(originalBookingTicketIsClosed));
		}

		if (errors?.reservationError === ReservationErrorType.SEAT_UNAVAILABLE) {
			setShownToast(true);

			const lost = errors.seatLabels ?? [];
			const named = lost.length > 0
				? `${lost.length === 1 ? 'Seat' : 'Seats'} ${lost.join(', ')} ${lost.length === 1 ? 'was' : 'were'} taken`
				: 'One of your seats was taken';

			newAlertModal(
				'Seat no longer available',
				`${named} while you were booking. Please choose again — the rest of your booking is unchanged.`,
				{
					cancelText: 'Choose another seat',
					buttonColour: Colors.Black,
				},
			).then(() => {
				// Fetched fresh rather than trusted: the cached map is what said the seat was free, and
				// it is held for 15 seconds. Refetching lets the seating step's own reconciliation drop
				// the seats that have gone, so the booker lands on an accurate plan rather than being
				// sent back to re-pick the seat they just lost.
				queryClient.invalidateQueries({ queryKey: ['seating-tab'] });
				navigate(`${baseUrl}/seating`);
			});
		}

		if (errors?.reservationError === ReservationErrorType.PAYMENT_ALREADY_TAKEN) {
			setShownToast(true);

			newAlertModal(
				'Payment already taken',
				'Payment has already been taken for this transaction. Please contact staff if this is an error or start a new transaction.',
				{
					cancelText: 'Ok',
					buttonColour: Colors.Black,
				},
			).then(navigateToBooking);
		}

		if (errors?.reservationError === ReservationErrorType.OTHER) {
			const rawErrorMsg = errors.message ?? 'Due to unknown error.';
			// The backend surfaces validation failures as .NET ArgumentExceptions, whose message
			// ends with a " (Parameter '...')" suffix that is noise to staff — strip it for display.
			const errorMsg =
				typeof rawErrorMsg === 'string' ? rawErrorMsg.replace(/\s*\(Parameter '[^']*'\)\s*$/, '') : rawErrorMsg;
			// A bulk failure that named the offending trips renders as a list, with its own explanation
			// and a way forward — a toast on top of it would be noise. But a bulk request can also fail
			// as a whole, with no trips to list: a validation rejection, a lock timeout. Returning here
			// unconditionally left those showing the reservation spinner forever, with the reason only
			// visible in the network tab.
			if (wizardData.isBulkBookingFlow && (errors.bulkBookingErrors?.length ?? 0) > 0) {
				return;
			}
			if (store.isStaff) {
				// Staff/manager/admin see the specific server-side reason so they can pinpoint
				// what was wrong with the booking — give them longer to read it.
				alertToast(`Could not create booking. Error: ${errorMsg}`, 'error', '', {
					autoClose: 8000,
				});
			} else {
				alertToast('Could not create booking. Please contact staff.', 'error', '', {
					autoClose: 3000,
				});
			}
			setShownToast(true);
			navigateToPreviousStep();
		}
	}, [
		errors,
		shownToast,
		wizardData.bulkBookingFilters,
		wizardData.bookingToEdit,
		store.isStaff,
		navigate,
		navigateToPreviousStep,
	]);

	/**
	 * Records the outcome of a reservation attempt, and drops the tickets tab's cached trips.
	 *
	 * The tickets tab caches its trip queries for a minute, and a reservation attempt invalidates that
	 * cache whichever way it goes:
	 *
	 * - It succeeded, so the trips it reserved now have fewer spaces than the cached copy says. A user
	 *   who steps back to the tickets tab — or alters the booking from there — was shown the capacity
	 *   as it stood before their own reservation took part of it.
	 * - It failed, which means a trip changed underneath the user: disabled, closed, or made private
	 *   since they picked it. Sending them back showed the very trip they were just refused, and it
	 *   stayed on screen until they refreshed the page by hand.
	 *
	 * Every tickets-tab query is keyed under `trips`, so one invalidation covers the departure and
	 * return lists and their single-trip alteration variants.
	 */
	const applyReservationResult = (result: ReservationErrorInformation) => {
		queryClient.invalidateQueries({ queryKey: ['trips'] });

		const failed = result.reservationError !== ReservationErrorType.NONE;
		onCompleteBookingAttempt(failed);
		setErrors(result);
	};

	const createReservations = async (
		data: BookingWizardData,
		bypassSpaceCheck: boolean = false,
	): Promise<ReservationErrorInformation> => {
		if (!data) {
			return {
				reservationError: ReservationErrorType.OTHER,
				invalidTrips: [],
			};
		}

		if (stringNotEmpty(data.userId)) {
			// On 200 these endpoints return `BookingCreationIds`; on 400 they return an error-message
			// string (they pass `validateStatus: () => true`, so both land here).
			let response: AxiosResponse<BookingCreationIds | string> | null;
			if (data.isBulkBookingFlow) {
				if (getFerryBookingTransactionIdFromStorage() === null) {
					response = await createBulkBooking(dataToBulkFerryTripBookingDto(data, bypassSpaceCheck, false));
				} else {
					response = await editCartBulkBooking(dataToBulkFerryTripBookingDto(data, bypassSpaceCheck, false));
				}
			} else if (data.bookingToEdit === undefined || data.bookingToEdit === '') {
				// Create new booking
				response = await createFerryBooking(dataToFerryTripBookingDto(data, bypassSpaceCheck, false));
			} else if (data.wizardMode === wizardModeOptions.CREATE) {
				// Booking is being built in the cart. bookingToEdit isn't cleared when the cart is
				// emptied, so if the cart's transaction has been cleared from storage it now points at
				// an expired booking — create a fresh booking instead of adding another alteration to
				// it (which would otherwise resurrect the old transaction and leave the booking with
				// >1 alteration, breaking the "only new bookings" check). Mirrors the bulk path above.
				if (getFerryBookingTransactionIdFromStorage() === null) {
					response = await createFerryBooking(dataToFerryTripBookingDto(data, bypassSpaceCheck, false));
				} else {
					// Edit booking in cart (booking is still in RESERVED state)
					response = await alterBooking(dataToFerryTripBookingDto(data, bypassSpaceCheck, false));
				}
			} else {
				// Alter booking (booking is in BOOKED state)
				response = await alterBooking(dataToFerryTripBookingDto(data, bypassSpaceCheck, false));
			}

			switch (response.status) {
				// Status will be 200 if the booking was successfully created
				case 200:
					if (response.data !== null) {
						if (data.wizardMode !== wizardModeOptions.ALTERATION) {
							clearOldWizardData();
						}

						if (response.data !== '') {
							// The endpoint will return the same transaction id when adding a second booking, so we can
							// safely just set the transaction id in session storage
							const creationIds = response.data as BookingCreationIds;
							setFerryBookingTransactionIdInStorage(creationIds.transactionId);
							const newData = new BookingWizardData({ ...data });

							newData.bookingToEdit = creationIds.bookingId;

							if (newData.wizardMode !== 'ALTERATION') {
								setOldWizardData(newData);
							}
							onUpdateWizardData(newData);

							// TODO: Re-enable parking reservation creation when booking wizard integration is complete
							// if (appConfigStore.parkingEnabled
							// 	&& parkingStore.quote && parkingStore.quote.length > 0) {
							// 	... parking reservation creation logic ...
							// }
						}
						return {
							reservationError: ReservationErrorType.NONE,
							invalidTrips: [],
						};
					}

					// This should never be reached but the failsafe is important. This will be reached if we get a
					// 200 but there is no data in the response. The method we hit will never do this and so we should
					// always hit the return within the response.data !== '' check
					return {
						reservationError: ReservationErrorType.OTHER,
						invalidTrips: [],
					};
				// Status will be 400 (BAD_REQUEST) if the input was malformed in any way. This shouldn't happen
				// when booking through the wizard but the endpoint can return this so we need to account for
				// this possibility
				case 400:
					return {
						reservationError: ReservationErrorType.OTHER,
						invalidTrips: [],
						message: typeof response.data === 'string' ? response.data : undefined,
					};
				case 401: {
					break;
				}
				// Status will be 408 (REQUEST_TIMEOUT) if we were unable to acquire the redis lock to perform
				// the space reservation checks. In the endpoint our attempts to get the lock would have `time`d
				// out, and completed without performing the checks or creating the booking
				case 408:
					return {
						reservationError: ReservationErrorType.OTHER,
						invalidTrips: [],
					};
				// Status will be 409 (CONFLICT) in two scenarios. These are when the space reservation checks
				// fail. We differentiate these based on the error
				// message which is returned because there wasn't a different error code which made sense to use
				// for either of these scenarios
				case 409:
					if (Array.isArray(response.data)) {
						const bulkErrors = response.data as InvalidBulkBookingErrorDto[];
						return {
							reservationError: ReservationErrorType.OTHER,
							invalidTrips: bulkErrors.map(x => x.journey.tripId),
							bulkBookingErrors: bulkErrors,
						};
					}

					// The create booking endpoint will always return this data structure for 409 errors
					// eslint-disable-next-line no-case-declarations
					const errorResponse = response.data as unknown as NoAvailableSpaceExceptionDto;

					if (errorResponse.error === 'Closed') {
						return {
							reservationError: ReservationErrorType.CLOSED,
							invalidTrips: errorResponse.invalidTrips,
						};
					}
					if (errorResponse.error === 'Unavailable') {
						return {
							reservationError: ReservationErrorType.UNAVAILABLE,
							invalidTrips: errorResponse.invalidTrips,
						};
					}
					if (errorResponse.error === ReservationErrorType.DISABLED_TRIP) {
						return {
							reservationError: ReservationErrorType.DISABLED_TRIP,
							invalidTrips: [],
						};
					}
					if (errorResponse.error === 'Other' && wizardData.isBulkBookingFlow) {
						return {
							reservationError: ReservationErrorType.OTHER,
							invalidTrips: errorResponse.invalidTrips,
						};
					}
					if (errorResponse.error === 'PaymentAlreadyTaken') {
						return {
							reservationError: ReservationErrorType.PAYMENT_ALREADY_TAKEN,
							invalidTrips: [],
						};
					}
					if (errorResponse.error === 'BookingNotEditable') {
						return {
							reservationError: ReservationErrorType.BOOKING_NOT_EDITABLE,
							invalidTrips: [],
							message: errorResponse.message,
						};
					}
					if (errorResponse.error === 'SeatUnavailable') {
						return {
							reservationError: ReservationErrorType.SEAT_UNAVAILABLE,
							invalidTrips: [],
							seatLabels: errorResponse.seatLabels ?? [],
						};
					}
					if (errorResponse.error === 'Overbooked') {
						return {
							...errorResponse,
							reservationError: ReservationErrorType.CARGO,
							invalidTrips: errorResponse.invalidTrips,
						};
					}
			}
			// If we get here, we are dealing with a duplicate. this scenario will be hit by going back and then
			// moving forward through the wizard again. We need the user to be able to progress in this scenario
			// so we can just have the component progress to the next step without any additional handling
			return {
				reservationError: ReservationErrorType.NONE,
				invalidTrips: [],
			};
		}

		// We shouldn't ever get here because the status code cases will sort everything out for us. We do need
		// to have a fallback for the compiler to not get cranky, and if we get here then we didn't get a 200,
		// which means that the entity wasn't created/updated.
		return {
			reservationError: ReservationErrorType.OTHER,
			invalidTrips: [],
		};
		// Only data is a dependency as adding setReservationErrors, setReservationErrorsAndGoToNextStep caused
		// multiple versions of the same booking to be made and reserved.
	};

	useEffect(() => {
		if (bookingCreationAttempted.current) {
			return;
		}
		bookingCreationAttempted.current = true;

		let isDuplicateAlteration = false;
		if (oldWizardData !== null && oldWizardData.wizardMode === 'ALTERATION') {
			isDuplicateAlteration = !ferryBookingWizardDataHasChanged(wizardData, oldWizardData);
		}

		if (!isDuplicateAlteration) {
			createReservations(wizardData).then(applyReservationResult);
		} else if (isDuplicateAlteration) {
			alertToast('No changes made to booking', 'error');
			navigateToPreviousStep();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Navigate to next step when booking succeeds - in useEffect to avoid calling during render
	useEffect(() => {
		if (errors?.reservationError === ReservationErrorType.NONE) {
			navigateToNextStep();
		}
	}, [errors, navigateToNextStep]);

	// Error handling is now in useEffect to avoid setState during render
	if (!shownToast && errors?.reservationError === ReservationErrorType.DISABLED_TRIP) {
		return <></>;
	}

	const clickCross = async (capacityCategory: CapacityCategory) => {
		if (capacityCategory === 'AddOn') {
			navigate(`${baseUrl}/add-ons`);
		} else if (store.can('OverbookInBookingWizard')) {
			navigateToTickets();
		} else {
			navigateToPreviousStep();
		}
	};

	/**
	 * The trips that would be left if the refused ones were dropped. When every selected trip was
	 * refused this is empty, and there is nothing to book — the server rejects an empty trip list
	 * (`IsBulkBookingDtoValid`) with a 400 that surfaces as the generic "could not create booking"
	 * toast, back on the previous step with the selection unchanged.
	 */
	const remainingBulkTripIds = (wizardData.bulkBookingFilters?.tripIds ?? []).filter(
		id => !errors.invalidTrips.includes(id),
	);

	const handleRemoveInvalidTripsAndProceed = () => {
		if (wizardData.isBulkBookingFlow) {
			// Nothing survives the removal, so retrying would only produce a rejection. The list offers
			// "Choose different trips" instead of "proceed" in this state; this guard covers the path
			// being reached any other way.
			if (remainingBulkTripIds.length === 0) {
				chooseDifferentBulkTrips();
				return;
			}

			const newFilters = { ...wizardData.bulkBookingFilters };

			// Remove the invalid trips from the list
			newFilters.tripIds = remainingBulkTripIds;

			const newWizardData = new BookingWizardData({
				...wizardData,
				bulkBookingFilters: newFilters as BulkBookingFilters,
			});

			// Update wizard data
			onUpdateWizardData(newWizardData);

			// Reset errors and retry
			setErrors({
				reservationError: ReservationErrorType.LOADING,
				invalidTrips: [],
				message: '',
			});

			createReservations(newWizardData).then(applyReservationResult);
		}
	};

	/**
	 * "Choose different trips", offered when every selected trip was refused.
	 *
	 * Deliberately not `cancelBulkBooking`: that clears `bulkBookingFilters`, and `isBulkBookingFlow`
	 * is simply `!!bulkBookingFilters`, so clearing it drops the wizard out of bulk mode altogether
	 * and lands on the single-trip tickets step. Here the user still wants a bulk booking — just of
	 * different trips — so the filters are kept and they go back to the criteria step to adjust the
	 * search.
	 *
	 * `tripIds` is cleared because the ids it holds are the trips that were just refused. The review
	 * step repopulates it from a fresh search, so this only matters if the user leaves before
	 * reaching it.
	 */
	const chooseDifferentBulkTrips = () => {
		onUpdateWizardData(
			new BookingWizardData({
				...wizardData,
				bulkBookingFilters: {
					...wizardData.bulkBookingFilters!,
					tripIds: [],
				} as BulkBookingFilters,
			}),
		);
		navigate(`${baseUrl}/bulk-tickets`);
	};

	const cancelBulkBooking = async () => {
		onUpdateWizardData(new BookingWizardData({ ...wizardData, bulkBookingFilters: undefined }));
		navigate('/booking-wizard/tickets');
	};

	const clickSelect = async (capacityCategory: CapacityCategory) => {
		const newData = new BookingWizardData({ ...wizardData });

		// Confirming the overbooking prompt resends with the space check bypassed, which the server
		// honours for OverbookInBookingWizard - everyone else goes back and picks another sailing.
		if (store.can('OverbookInBookingWizard')) {
			setErrors({ reservationError: ReservationErrorType.LOADING, invalidTrips: [] });

			const bypassSpaceCheck = true;
			createReservations(newData, bypassSpaceCheck).then(applyReservationResult);

			return;
		}

		if (capacityCategory !== 'AddOn') {
			errors?.invalidTrips.forEach(tripId => {
				if (newData.departureTicketId === tripId) {
					newData.departureTicketId = '';
					newData.departingJourney = undefined;
					devLog('DEV0013: Deselect departing ticket');
				}
				if (newData.returningTicketId === tripId) {
					newData.returningTicketId = '';
					newData.returningJourney = undefined;
					devLog('DEV0014: Deselect returning ticket');
				}
			});
			onUpdateWizardData(newData);
			navigateToTickets();
		} else {
			onUpdateWizardData(newData);
			navigate(`${baseUrl}/add-ons`);
		}
	};

	const showInvalidBulkTrips =
		errors?.reservationError === ReservationErrorType.OTHER &&
		wizardData.isBulkBookingFlow &&
		errors.invalidTrips.length > 0;

	return (
		<>
			{!showInvalidBulkTrips && <WaitingReservationLoader />}

			{showInvalidBulkTrips && (
				<InvalidBulkTripsList
					bulkBookingErrors={errors.bulkBookingErrors!}
					remainingTripCount={remainingBulkTripIds.length}
					onRemoveAndProceed={handleRemoveInvalidTripsAndProceed}
					onChooseDifferentTrips={chooseDifferentBulkTrips}
					onCancel={cancelBulkBooking}
				/>
			)}

			<OverbookingModalManager
				onClickCross={clickCross}
				onClickSelect={(capacityCategory: CapacityCategory) => clickSelect(capacityCategory)}
				departingOverbookingWarnings={errors?.departingOverbookingWarnings}
				returningOverbookingWarnings={errors?.returningOverbookingWarnings}
			/>
		</>
	);
}
